import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ShippingTier {
  id: string;
  label: string;
  min_weight_kg: number;
  max_weight_kg: number | null;
  price: number;
  position: number;
  active: boolean;
}

export interface ShippingSettings {
  id: string;
  free_shipping_threshold: number;
}

export const useShippingTiers = () =>
  useQuery({
    queryKey: ["shipping-tiers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shipping_tiers" as any)
        .select("*")
        .eq("active", true)
        .order("position");
      if (error) throw error;
      return (data ?? []) as unknown as ShippingTier[];
    },
  });

export const useShippingSettings = () =>
  useQuery({
    queryKey: ["shipping-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shipping_settings" as any)
        .select("*")
        .limit(1)
        .single();
      if (error) throw error;
      return data as unknown as ShippingSettings;
    },
  });

export const useAllShippingTiers = () =>
  useQuery({
    queryKey: ["shipping-tiers-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shipping_tiers" as any)
        .select("*")
        .order("position");
      if (error) throw error;
      return (data ?? []) as unknown as ShippingTier[];
    },
  });

/** Calculate shipping cost based on subtotal and cart weight */
export function calculateShipping(
  subtotal: number,
  cartWeightKg: number,
  tiers: ShippingTier[],
  freeThreshold: number
): { cost: number; tierLabel: string; isFree: boolean } {
  if (freeThreshold > 0 && subtotal >= freeThreshold) {
    return { cost: 0, tierLabel: "Free shipping", isFree: true };
  }

  // Find matching tier by weight
  const tier = tiers.find(
    (t) =>
      cartWeightKg >= t.min_weight_kg &&
      (t.max_weight_kg === null || cartWeightKg < t.max_weight_kg)
  );

  if (tier) {
    return { cost: tier.price, tierLabel: tier.label, isFree: false };
  }

  // Fallback: use highest tier if weight exceeds all
  const highest = tiers[tiers.length - 1];
  if (highest) {
    return { cost: highest.price, tierLabel: highest.label, isFree: false };
  }

  // No tiers configured – default flat rate
  return { cost: 4.99, tierLabel: "Standard", isFree: false };
}
